import { Stack } from "@mui/material";
import Box from "@mui/material/Box";
import Grid from "@mui/material/Grid";
import LanguageContext from "components/LanguageContext";
import HelpAndShare from "components/LayoutFront/HelpAndShare";
import Wrapper from "components/LayoutFront/Wrapper";
import SiteContext from "components/SiteContext";
import article from "components/templates/article/article";
import articleDsfr from "components/templates/articleDsfr/articleDsfr";
import contact from "components/templates/contact/contact";
import help from "components/templates/help/help";
import helpDsfr from "components/templates/helpDsfr/helpDsfr";
import news from "components/templates/news/news";
import process from "components/templates/process/process";
import processDsfr from "components/templates/processDsfr/processDsfr";
import SearchCard from "components/templatesComponents/SearchCard";
import SearchFilters from "components/templatesComponents/SearchFilters";
import { useSearch } from "hooks/page.hook";
import PropTypes from "prop-types";
import React, { useCallback, useContext, useEffect, useMemo, useState } from "react";
import Helmet from "react-helmet";
import scrollIntoView from "scroll-into-view-if-needed";
import { getTranslateIndex } from "utils/commonUtils";
import t from "utils/locales/translation.json";
import { getQueryParams } from "utils/urlUtils";

const noResultList = ["no_result_message1", "no_result_message2", "no_result_message3"];

const specificTemplates = [
  article.key,
  articleDsfr.key,
  contact.key,
  help.key,
  helpDsfr.key,
  news.key,
  process.key,
  processDsfr.key,
];

const ResultsSearch = (props) => {
  const { location } = props;
  const queryParams = getQueryParams(location.search);
  const { language } = useContext(LanguageContext);
  const site = useContext(SiteContext);
  const { name: siteName } = site || {};

  const [templatesSelected, setTemplatesSelected] = useState([]);

  const { data: result } = useSearch({ query: queryParams.q, lang: language, from: 0, size: 100 });

  const { hits } = result || {};
  const { hits: results = [], total } = hits || {};

  const getTemplatePagesCount = useCallback(
    (templates) => {
      const pagesCount = results.filter((res) => {
        const { _source: page } = res;
        if (templates.includes("OTHER")) {
          return !specificTemplates.filter((temp) => !templates.includes(temp)).includes(page.template);
        }
        return templates.includes(page.template);
      });
      return pagesCount.length;
    },
    [results],
  );

  const tabs = useMemo(
    () => [
      {
        title: t[language].search.process,
        templates: [process.key, processDsfr.key],
        pages: getTemplatePagesCount([process.key, processDsfr.key]),
      },
      { title: t[language].search.news, templates: [news.key], pages: getTemplatePagesCount([news.key]) },
      {
        title: t[language].search.article,
        templates: [article.key, articleDsfr.key],
        pages: getTemplatePagesCount([article.key, articleDsfr.key]),
      },
      {
        title: t[language].search.help,
        templates: [contact.key, help.key, helpDsfr.key],
        pages: getTemplatePagesCount([contact.key, help.key, helpDsfr.key]),
      },
      { title: t[language].search.others_tab_title, templates: ["OTHER"], pages: getTemplatePagesCount(["OTHER"]) },
    ],
    [language, getTemplatePagesCount],
  );

  const scrollSent = () => {
    scrollIntoView(document.getElementById("search-results-title"), {
      block: "center",
      inline: "center",
      behavior: "smooth",
    });
  };

  const handleSelectedTemplates = useCallback(
    (selectedTemplate) => {
      if (templatesSelected.some((template) => selectedTemplate.includes(template))) {
        setTemplatesSelected(templatesSelected.filter((template) => !selectedTemplate.includes(template)));
      } else {
        setTemplatesSelected([...templatesSelected, ...selectedTemplate]);
      }
      scrollSent();
    },
    [templatesSelected],
  );

  useEffect(() => {
    setTemplatesSelected([]);
  }, [queryParams.q]);

  const filteredResults = useMemo(() => {
    if (!templatesSelected.length) {
      return results;
    }
    if (templatesSelected.includes("OTHER")) {
      return results.filter((res) => {
        const { _source: page } = res;
        return !specificTemplates.filter((temp) => !templatesSelected.includes(temp)).includes(page.template);
      });
    }
    return results.filter((res) => {
      const { _source: page } = res;
      return templatesSelected.includes(page.template);
    });
  }, [results, templatesSelected]);

  return (
    result && (
      <>
        <Helmet>
          <title>
            {t[language].search.balise_title[getTranslateIndex(filteredResults.length)]}
            {siteName === "ANTS" ? "" : " - "}
            {t[language].site_name[siteName]} - France Titres (ANTS)
          </title>
        </Helmet>
        {total.value > 0 && (
          <Box sx={{ display: { lg: "none" } }}>
            <Box component="section" className="fr-accordion">
              <h3 className="fr-accordion__title">
                <Box
                  component="button"
                  className="fr-accordion__btn"
                  aria-expanded="false"
                  aria-controls="accordion-search-filter"
                  sx={{ "& img": { mr: 1 } }}
                >
                  <img src="/filter-icon.svg" alt="" />
                  {t[language].search.filter_title}
                </Box>
              </h3>
              <Box className="fr-collapse" id="accordion-search-filter" sx={{ flex: 1, mx: 2 }}>
                <SearchFilters
                  title={t[language].search.topic}
                  tabs={tabs}
                  templatesSelected={templatesSelected}
                  handleSelectedTemplates={handleSelectedTemplates}
                />
              </Box>
            </Box>
          </Box>
        )}
        <Wrapper sx={{ px: { xs: 2, lg: 0 } }}>
          {total.value > 0 && (
            <Grid container spacing={5}>
              <Grid size={{ xs: 12, lg: 3 }}>
                <Box sx={{ display: { xs: "none", lg: "block" }, position: "sticky", top: 16 }}>
                  <h6>{t[language].search.filter_title}</h6>
                  <SearchFilters
                    title={t[language].search.topic}
                    tabs={tabs}
                    templatesSelected={templatesSelected}
                    handleSelectedTemplates={handleSelectedTemplates}
                  />
                </Box>
              </Grid>
              <Grid size={{ xs: 12, lg: 9 }}>
                <h2 id="search-results-title" role="status">
                  {t[language].search.pre_title}
                  {filteredResults.length}
                  {t[language].search.post_title[getTranslateIndex(filteredResults.length)]} : &quot;{queryParams.q}
                  &quot;
                </h2>
                <Stack spacing={2} role="list" sx={{ mt: 3 }}>
                  {filteredResults.map((res) => {
                    const { _source: page } = res;
                    return <SearchCard page={page} key={page.title} />;
                  })}
                </Stack>
              </Grid>
            </Grid>
          )}
          {total.value === 0 && (
            <>
              <h2 role="status">
                {t[language].search.no_result_title} : &quot;{queryParams.q}&quot;
              </h2>
              <ul>
                {noResultList.map((item) => (
                  <li key={item} className="fr-text--lg fr-mb-0">
                    {t[language].common.search[item]}
                  </li>
                ))}
              </ul>
            </>
          )}
        </Wrapper>
        <HelpAndShare alwaysDisplayHelp />
      </>
    )
  );
};

ResultsSearch.propTypes = {
  location: PropTypes.shape().isRequired,
};

export default ResultsSearch;
